import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { executeAwaitableHandlerChain, Handler, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { CatalogPage } from "@insite/client-framework/Services/CategoryService";
import {
    GetProductCollectionApiV2Parameter,
    ProductExpandTokens,
} from "@insite/client-framework/Services/ProductServiceV2";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import {
    chain,
    DispatchLoadProducts,
    ParseQueryParameter,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import setFilterQuery from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetFilterQuery";
import { getProductListDataViewProperty,isLoadMore } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductListStateExtension } from "@tecumseh/Handlers/ProductListStateExtension";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import qs, { ParsedQs } from "qs";

interface Parameter {
    queryString: string;
    path: string;
    isSearch: boolean;
}

export interface DisplayProductsResult {
    products?: ProductModel[];
    productInfosByProductId: SafeDictionary<ProductInfo>;
    productFilters: ProductListStateExtension;
    isFiltered: boolean;
    unfilteredApiParameter?: GetProductCollectionApiV2Parameter;
    filteredApiParameter?: GetProductCollectionApiV2Parameter;
    catalogPage?: CatalogPage;
}

interface Props {
    apiParameter: GetProductCollectionApiV2Parameter;
    idByPath?: SafeDictionary<string>;
    result: DisplayProductsResult;
    pricingLoaded?: true;
    expandTokens?: ProductExpandTokens;
}

type HandlerType = Handler<Parameter, Props>;

export const ParseQueryParameterExtension: HandlerType = props => {
    const { queryString } = props.parameter;
    const parsedQuery = qs.parse(queryString.startsWith("?") ? queryString.substr(1) : queryString);
    const splitCommaSeparated = (
    value?: string | string[] | (string | ParsedQs)[] | ParsedQs | null
        ): string[] | undefined => {
            if (typeof value === "string") {
                return value.split(",");
            }
            if (Array.isArray(value)) {
                // Flatten all string values, split by comma, and filter out non-strings
                const result: string[] = [];
                value.forEach(v => {
                    if (typeof v === "string") {
                        result.push(...v.split(","));
                    }
                });
                return result.length > 0 ? result : undefined;
            }
            // If it's a ParsedQs object or null/undefined, return undefined
            return undefined;
        };
    const {
        criteria,
        query,
        page,
        sort,
        includeSuggestions,
        stockedItemsOnly,
        previouslyPurchasedProducts,
        categoryId,
        brandIds,
        productLineIds,
        priceFilters,
        attributeValueIds,
        searchWithinQueries,
        isInitialLoad,
        IncludeSubcategories,
        activeTab,
    } = parsedQuery;

    let pageSize = parsedQuery.pageSize;

    if (!pageSize) {
        pageSize = getCookie("ProductList-PageSize");
    }

    if (isLoadMore(props.getState())) {
        pageSize = String(getSettingsCollection(props.getState()).productSettings.numberOfProductsToLoad);
    }

    const { catalogPage } = props.result;

    props.result.productFilters = {
        query:
        typeof criteria === "string"
            ? criteria
            : typeof query === "string"
                ? query
                : undefined,
        page: page ? parseInt(page as string, 10) : undefined,
        pageSize: pageSize ? parseInt(pageSize as string, 10) : undefined,
        sort: typeof sort === "string" ? sort : undefined,
        includeSuggestions: includeSuggestions !== undefined ? includeSuggestions === "true" : undefined,
        stockedItemsOnly: stockedItemsOnly !== undefined ? stockedItemsOnly === "true" : undefined,
        previouslyPurchasedProducts:
            previouslyPurchasedProducts !== undefined ? previouslyPurchasedProducts === "true" : undefined,
        pageCategoryId: catalogPage?.categoryId,
        pageBrandId: catalogPage?.brandId || undefined,
        pageProductLineId: catalogPage?.productLineId || undefined,
        categoryId: typeof categoryId === "string" ? categoryId : undefined,
        brandIds: splitCommaSeparated(brandIds),
        productLineIds: splitCommaSeparated(productLineIds),
        priceFilters: splitCommaSeparated(priceFilters),
        attributeValueIds: splitCommaSeparated(attributeValueIds),
        searchWithinQueries: splitCommaSeparated(searchWithinQueries),
        isInitialLoad: typeof isInitialLoad === "string" ? isInitialLoad === "true" : undefined,
        // Removed this flag as preselected attributes are not required
        IncludeSubcategories: true,
        activeTab: typeof activeTab === "string" ? activeTab : undefined,
    };
};

replaceInChain(chain, ParseQueryParameter, ParseQueryParameterExtension);

export const DispatchLoadProductsExtension: HandlerType = async props => {
    const state = props.getState();
    props.result.products = getProductsDataView(state, props.apiParameter).value;
    if (props.result.products) {
        const attributeTypeFacetIds = getProductListDataViewProperty(state, "attributeTypeFacets")?.map(
            o => o.attributeTypeId,
        );

        let attributeTypeIds: string[] = [];
        props.result.products.forEach(product => {
            const ids = product.attributeTypes?.map(o => o.id) || [];
            attributeTypeIds = attributeTypeIds.concat(ids);
        });
        if (attributeTypeIds.length === attributeTypeFacetIds?.length) {
            const isAllAttributesAssignedToCategory = attributeTypeIds.every(
                o => attributeTypeFacetIds.indexOf(o) > -1,
            );
            if (isAllAttributesAssignedToCategory) {
                return;
            }
        }
    }

    let preSelectAttrIds: string[] = [];
    const setPreSelectAttributes = (preAttributes: string) => {
        preSelectAttrIds = preAttributes.split(",");
    };
    props.result.products = await executeAwaitableHandlerChain(
        loadProducts,
        { ...props.apiParameter, 
            setPreSelectAttributes,
            isLoadMore: isLoadMore(props.getState()) && !!props.apiParameter.page && props.apiParameter.page > 1 } as any,
        props,
    );

    const { categoryId, isPreselectDataLoaded } = (state as any).customPreselectAttributeReducer;
    if (preSelectAttrIds.length > 0 && categoryId !== props.apiParameter.categoryId) {
        props.result.productFilters.attributeValueIds = preSelectAttrIds;
        (props as any).dispatch({
            type: "Custom/PreSelectAttributes/SetData",
            payload: {
                categoryId: props.apiParameter.categoryId,
                isPreselectDataLoaded: true,
            },
        });
        (props as any).dispatch({
            type: "Pages/ProductList/SetProductFilters",
            result: props.result.productFilters,
        });
        (props as any).dispatch(setFilterQuery());
    } else if (preSelectAttrIds.length === 0) {
        (props as any).dispatch({
            type: "Custom/PreSelectAttributes/SetData",
            payload: {
                categoryId: "",
                isPreselectDataLoaded: false,
            },
        });
    }
};

replaceInChain(chain, DispatchLoadProducts, DispatchLoadProductsExtension);
